import { Drawer, Table } from "@caprikorn/dynamo-ui";
import { createColumnHelper } from "@tanstack/react-table";
import { type FC, type SubmitEvent, useId, useMemo, useState } from "react";

import type { CustomerAllByGroup } from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import {
  computeInitialSelectedState,
  formatAuthorizations,
} from "../AccessManager.utils";
import classes from "./AuthorizationsDrawer.module.css";
import type { AuthorizationsDrawerProps } from "./AuthorizationsDrawer.types";
import { AuthorizationsTableInitialSelection } from "../AuthorizationsTableInitialSelection/AuthorizationsTableInitialSelection";

const getRowId = (row: CustomerAllByGroup) => row.customerId;

export const AuthorizationsDrawer: FC<AuthorizationsDrawerProps> = ({
  customerList = [],
  onHide,
  currentAuthorizations,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useCustomTranslation("main");
  const formId = useId();
  const initialSelectedState = useMemo(
    () => computeInitialSelectedState(currentAuthorizations, customerList),
    [currentAuthorizations, customerList],
  );

  const [selectedCustomers, setSelectedCustomers] =
    useState<CustomerAllByGroup[]>(initialSelectedState);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formattedAuthorizations = formatAuthorizations(selectedCustomers);
    onSubmit(formattedAuthorizations);
  };

  const columnHelper = createColumnHelper<CustomerAllByGroup>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("customerName", {
        header: t("userDetails.authorization.label"),
        cell: ({ row }) =>
          `${row.original.customerName} - ${row.original.customerId}`,
      }),
    ],
    [columnHelper, t],
  );

  return (
    <Drawer onClose={onHide}>
      <Drawer.Header title={t("userDetails.authorization.drawer")} />
      <Drawer.Body>
        <form className={classes.form} id={formId} onSubmit={handleSubmit}>
          <Table
            className={classes.table}
            columns={columns}
            data={customerList}
            getRowId={getRowId}
            hasRowSelection
            noPagination
            onSelectionChange={(selectedRows) =>
              setSelectedCustomers(selectedRows.map((row) => row.original))
            }
          >
            <Table.ActionBar>
              <Table.ActionBar.FilterBar
                hasGlobalFilter
                searchPlaceholder={t("userDetails.authorization.placeholder")}
              />
            </Table.ActionBar>
            <AuthorizationsTableInitialSelection
              initialSelectedState={initialSelectedState}
            />
            <Table.Content />
          </Table>
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        <Drawer.Footer.ConfirmButton
          disabled={selectedCustomers.length === 0}
          form={formId}
          isLoading={isSubmitting}
          type="submit"
        />
        <Drawer.Footer.CancelButton />
      </Drawer.Footer>
    </Drawer>
  );
};
